import { useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Upload,
  type TableColumnsType,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { usePermissions } from '../../auth/store';
import { formatWhen } from '../listeners/listenerStats';
import { WelcomeRulesBannerPreview } from './WelcomeRulesBannerPreview';
import { SETTINGS_KEY, WelcomeRulesSettingsCard } from './WelcomeRulesSettingsCard';
import {
  bannerRules,
  ruleLanguages,
  isValidLanguageCode,
  APP_UI_LANGUAGES,
  languageLabel,
  DEFAULT_LANGUAGE,
  CONTENT_MAX,
  DEFAULT_SETTINGS,
  EMPTY_FORM,
  formBody,
  formError,
  formFromRule,
  imageProblem,
  moveRule,
  TITLE_MAX,
  type BannerSettingsView,
  type RuleForm,
  type WelcomeRule,
} from './welcomeRules';

const KEY = ['admin', 'welcome-rules', 'list'];

function errMsg(e: unknown, fallback: string) {
  return (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? fallback;
}

/**
 * Welcome Rules — the rules banner a listener sees when they open the app
 * (at most twice a day each; the API keeps that count per listener).
 */
export function WelcomeRulesPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = usePermissions();
  const canEdit = can('welcome_rules.edit');
  const canToggle = can('welcome_rules.toggle');

  /** null = closed, 'new' = creating, otherwise the rule being edited. */
  const [editing, setEditing] = useState<WelcomeRule | 'new' | null>(null);
  const [form, setForm] = useState<RuleForm>(EMPTY_FORM);
  /**
   * Which language's set is on screen. Rules are authored per language (like
   * voice sentences) and a listener only ever sees ONE language's set, so the
   * table, the reordering and the preview are all scoped to this.
   */
  const [language, setLanguage] = useState(DEFAULT_LANGUAGE);

  const { data, isLoading } = useQuery({
    queryKey: KEY,
    queryFn: async () => (await api.get<WelcomeRule[]>('/admin/welcome-rules')).data,
  });
  const allRules = useMemo(() => data ?? [], [data]);
  const rules = useMemo(
    () => allRules.filter((r) => r.languageCode === language),
    [allRules, language],
  );
  /** The app's UI languages, every language that already has rules, and the one being viewed. */
  const languages = useMemo(() => {
    const known = ruleLanguages(allRules);
    const extra = [...APP_UI_LANGUAGES.map((l) => l.code), language]
      .filter((c) => !known.includes(c))
      .sort();
    return [...known, ...extra];
  }, [allRules, language]);
  const settingsQuery = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: async () => (await api.get<BannerSettingsView>('/admin/welcome-rules/settings')).data,
  });
  // Until the settings load, the form limits fall back to the shipped defaults
  // (the API re-checks every upload against the real ones).
  const settings = settingsQuery.data ?? DEFAULT_SETTINGS;
  const refresh = () => qc.invalidateQueries({ queryKey: KEY });

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, languageCode: language });
    setEditing('new');
  };
  const openEdit = (rule: WelcomeRule) => {
    setForm(formFromRule(rule));
    setEditing(rule);
  };

  const save = useMutation({
    mutationFn: () =>
      editing === 'new'
        ? api.post('/admin/welcome-rules', formBody(form))
        : api.patch(`/admin/welcome-rules/${(editing as WelcomeRule).id}`, formBody(form)),
    onSuccess: () => {
      message.success(editing === 'new' ? 'Welcome rule created' : 'Welcome rule updated');
      setEditing(null);
      void refresh();
    },
    onError: (e) => message.error(errMsg(e, 'Could not save the rule')),
  });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.post(`/admin/welcome-rules/${id}/${active ? 'activate' : 'deactivate'}`),
    onSuccess: () => void refresh(),
    onError: (e) => message.error(errMsg(e, 'Could not change the status')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/welcome-rules/${id}`),
    onSuccess: () => {
      message.success('Welcome rule deleted');
      void refresh();
    },
    onError: (e) => message.error(errMsg(e, 'Could not delete the rule')),
  });

  const reorder = useMutation({
    // Scoped: the table shows one language, so the order it sends is that
    // language's. The API validates the ids against that same set.
    mutationFn: (ids: string[]) =>
      api.put('/admin/welcome-rules/order', { ids, languageCode: language }),
    onSuccess: () => void refresh(),
    onError: (e) => {
      message.error(errMsg(e, 'Could not reorder the rules'));
      void refresh();
    },
  });

  /**
   * Image goes browser → storage on a short-lived URL; the server derives the
   * key. The rule only points at it once the form is saved.
   */
  const uploadImage = useMutation({
    mutationFn: async (file: File) => {
      const bad = imageProblem(file, settings);
      if (bad) throw new Error(bad);
      const { data: minted } = await api.post<{ key: string; url: string }>(
        '/admin/welcome-rules/image/upload-url',
        { contentType: file.type, sizeBytes: file.size },
      );
      const res = await fetch(minted.url, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: file,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      return { key: minted.key, preview: URL.createObjectURL(file) };
    },
    onSuccess: ({ key, preview }) => setForm((f) => ({ ...f, imageKey: key, imageUrl: preview })),
    onError: (e) => message.error(e instanceof Error ? e.message : errMsg(e, 'Could not upload the image')),
  });

  const ids = rules.map((r) => r.id);
  const move = (id: string, direction: -1 | 1) => {
    const next = moveRule(ids, id, direction);
    if (next !== ids) reorder.mutate(next);
  };

  const columns: TableColumnsType<WelcomeRule> = [
    {
      title: 'Rule',
      render: (_: unknown, r) => (
        <Space align="start">
          {r.imageUrl ? (
            <img src={r.imageUrl} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover' }} />
          ) : (
            r.icon && <span style={{ fontSize: 20 }}>{r.icon}</span>
          )}
          <Space direction="vertical" size={0}>
            <span style={{ fontWeight: 600 }}>{r.title}</span>
            {r.content && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis={{ tooltip: r.content }}>
                {r.content.length > 90 ? `${r.content.slice(0, 90)}…` : r.content}
              </Typography.Text>
            )}
          </Space>
        </Space>
      ),
    },
    {
      title: 'Status',
      width: 150,
      render: (_: unknown, r) => (
        <Space>
          <Tag color={r.isActive ? 'green' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Tag>
          <Switch
            size="small"
            aria-label={`${r.isActive ? 'Disable' : 'Enable'} ${r.title}`}
            checked={r.isActive}
            loading={toggle.isPending && toggle.variables?.id === r.id}
            disabled={toggle.isPending || !canToggle}
            onChange={(v) => toggle.mutate({ id: r.id, active: v })}
          />
        </Space>
      ),
    },
    {
      title: 'Order',
      width: 120,
      align: 'right',
      render: (_: unknown, r, i) => (
        <Space size={4}>
          <span>{i + 1}</span>
          <Button
            size="small"
            type="text"
            icon={<ArrowUpOutlined />}
            aria-label={`Move ${r.title} up`}
            disabled={!canEdit || i === 0 || reorder.isPending}
            onClick={() => move(r.id, -1)}
          />
          <Button
            size="small"
            type="text"
            icon={<ArrowDownOutlined />}
            aria-label={`Move ${r.title} down`}
            disabled={!canEdit || i === rules.length - 1 || reorder.isPending}
            onClick={() => move(r.id, 1)}
          />
        </Space>
      ),
    },
    { title: 'Created', width: 150, render: (_: unknown, r) => formatWhen(r.createdAt) },
    { title: 'Updated', width: 150, render: (_: unknown, r) => formatWhen(r.updatedAt) },
    {
      title: 'Actions',
      width: 170,
      render: (_: unknown, r) => (
        <Space size={4}>
          <Can permission="welcome_rules.edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
              Edit
            </Button>
          </Can>
          <Can permission="welcome_rules.delete">
            <Popconfirm
              title="Delete this rule?"
              description="Listeners will stop seeing it. This cannot be undone."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => remove.mutate(r.id)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} aria-label={`Delete ${r.title}`} />
            </Popconfirm>
          </Can>
        </Space>
      ),
    },
  ];

  const problem = formError(form);
  // The preview while editing: the saved list with the draft swapped in.
  const draftPreview = useMemo(() => {
    if (editing === null) return bannerRules(allRules, language);
    const draft = {
      id: editing === 'new' ? 'draft' : editing.id,
      title: form.title.trim() || 'Rule title',
      content: form.content.trim(),
      icon: form.icon.trim() || null,
      imageUrl: form.imageUrl,
    };
    const scope = allRules.filter((r) => r.languageCode === form.languageCode);
    const active = bannerRules(scope.filter((r) => r.id !== draft.id), form.languageCode);
    if (!form.isActive) return active;
    if (editing === 'new') return [...active, draft];
    return bannerRules(
      scope.map((r) => (r.id === draft.id ? { ...r, ...draft, isActive: true } : r)),
      form.languageCode,
    );
  }, [editing, form, allRules]);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <Space direction="vertical" size={0}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Welcome Rules
          </Typography.Title>
          <Typography.Text type="secondary">
            {settings.enabled
              ? `Shown to each listener when they open the app — at most ${settings.dailyLimit} time${settings.dailyLimit === 1 ? '' : 's'} a day. Only active rules appear.`
              : 'The banner is switched off in the settings below — listeners do not see it.'}
          </Typography.Text>
        </Space>
        <Can permission="welcome_rules.create">
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Create Welcome Rule
          </Button>
        </Can>
      </Space>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <Card
            styles={{ body: { padding: 0 } }}
            title={
              <Space wrap>
                <span>Rules</span>
                <Select
                  aria-label="Language"
                  size="small"
                  style={{ minWidth: 140 }}
                  showSearch
                  value={language}
                  onSearch={(v) => {
                    const code = v.trim().toLowerCase();
                    if (isValidLanguageCode(code)) setLanguage(code);
                  }}
                  onChange={setLanguage}
                  options={languages.map((c) => ({ value: c, label: languageLabel(c) }))}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {rules.length === 0
                    ? `No rules in "${language}" — listeners whose language is ${language} see the English set.`
                    : `${rules.length} rule${rules.length === 1 ? '' : 's'} in "${language}"`}
                </Typography.Text>
              </Space>
            }
          >
            <Table
              rowKey="id"
              loading={isLoading}
              dataSource={rules}
              columns={columns}
              pagination={false}
              scroll={{ x: 820 }}
              locale={{
                emptyText: `No welcome rules in "${language}" yet — create the first one.`,
              }}
            />
          </Card>
        </Col>
        <Col xs={24} xl={8}>
          <Card title="Listener app preview">
            <WelcomeRulesBannerPreview
              rules={bannerRules(allRules, language)}
              defaultIcon={settings.defaultIcon}
            />
          </Card>
        </Col>
      </Row>

      <WelcomeRulesSettingsCard settings={settingsQuery.data} />

      <Modal
        title={editing === 'new' ? 'Create Welcome Rule' : 'Edit Welcome Rule'}
        open={editing !== null}
        width={760}
        onCancel={() => setEditing(null)}
        onOk={() => save.mutate()}
        okText={editing === 'new' ? 'Create' : 'Save'}
        okButtonProps={{ disabled: problem !== null || uploadImage.isPending, loading: save.isPending }}
        destroyOnClose
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={13}>
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              <div>
                <Typography.Text strong>Language</Typography.Text>
                <Select
                  aria-label="Rule language"
                  style={{ width: '100%' }}
                  showSearch
                  value={form.languageCode}
                  // A language that has no rules yet is typed in, exactly as
                  // the voice-sentence screen does it.
                  onSearch={(v) => setForm({ ...form, languageCode: v.trim().toLowerCase() })}
                  onChange={(v: string) => setForm({ ...form, languageCode: v })}
                  options={languages.map((c) => ({ value: c, label: languageLabel(c) }))}
                  status={isValidLanguageCode(form.languageCode) ? undefined : 'error'}
                  placeholder="ISO 639 code · e.g. en, ta, hi"
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Listeners see the set written in their own language; a language with no
                  rules falls back to the whole English set.
                </Typography.Text>
              </div>
              <div>
                <Typography.Text strong>Title</Typography.Text>
                <Input
                  aria-label="Rule title"
                  placeholder="e.g. Be respectful with users"
                  maxLength={TITLE_MAX}
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div>
                <Typography.Text strong>Description (optional)</Typography.Text>
                <Input.TextArea
                  aria-label="Rule description"
                  placeholder="A line or two explaining the rule"
                  rows={3}
                  maxLength={CONTENT_MAX}
                  showCount
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                />
              </div>
              <div>
                <Typography.Text strong>Icon (optional emoji)</Typography.Text>
                <Input
                  aria-label="Rule icon"
                  placeholder="e.g. 🎙️"
                  maxLength={16}
                  value={form.icon}
                  onChange={(e) => setForm({ ...form, icon: e.target.value })}
                />
              </div>
              <div>
                <Typography.Text strong>Image (optional — replaces the icon)</Typography.Text>
                <div>
                  <Space>
                    {form.imageUrl && (
                      <img src={form.imageUrl} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
                    )}
                    <Upload
                      accept={settings.imageTypes.join(',')}
                      showUploadList={false}
                      beforeUpload={(file) => {
                        uploadImage.mutate(file);
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />} loading={uploadImage.isPending}>
                        {form.imageKey ? 'Replace' : 'Upload'}
                      </Button>
                    </Upload>
                    {form.imageKey && (
                      <Button type="link" danger onClick={() => setForm({ ...form, imageKey: null, imageUrl: null })}>
                        Remove
                      </Button>
                    )}
                  </Space>
                </div>
              </div>
              <Space>
                <Switch
                  aria-label="Rule active"
                  checked={form.isActive}
                  onChange={(v) => setForm({ ...form, isActive: v })}
                />
                <span>{form.isActive ? 'Active — shown to listeners' : 'Inactive — hidden from listeners'}</span>
              </Space>
              {problem && form.title.length > 0 && <Typography.Text type="danger">{problem}</Typography.Text>}
            </Space>
          </Col>
          <Col xs={24} md={11}>
            <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 8 }}>
              Preview
            </Typography.Text>
            <WelcomeRulesBannerPreview rules={draftPreview} defaultIcon={settings.defaultIcon} />
          </Col>
        </Row>
      </Modal>
    </Space>
  );
}
